import * as Yup from 'yup';

/**
 * Drop-in replacement for the `validate.js` API used in this codebase.
 *
 * Why this exists: validate.js (0.13.1, last published) ships regex validators
 * vulnerable to ReDoS and has no upstream patch. This module reproduces the
 * exact subset of the validate.js API the app relies on — `validate()`,
 * `validate.single()`, custom validators via `validate.validators`, and the
 * `{ field: [fullMessage] }` error shape — while delegating the regex-based
 * validators (email/url) to yup, whose patterns are linear-time and safe.
 *
 * The non-regex validators (presence/length/numericality/format) are
 * re-implemented to match validate.js semantics (including default messages,
 * `%{count}`/`%{type}` interpolation, empty-value handling, and field-name
 * prettification) so existing forms and tests behave identically.
 */

// --- type helpers (mirroring validate.js) ---
const isDefined = (value) => value !== null && value !== undefined;
const isString = (value) => typeof value === 'string';
const isArray = (value) => Object.prototype.toString.call(value) === '[object Array]';
const isDate = (value) => value instanceof Date;
const isFunction = (value) => typeof value === 'function';
const isNumber = (value) => typeof value === 'number' && !Number.isNaN(value);
const isInteger = (value) => isNumber(value) && value % 1 === 0;
const isObject = (value) => value === Object(value);

const EMPTY_STRING_REGEXP = /^\s*$/;
const isEmpty = (value) => {
  if (!isDefined(value)) return true;
  if (isFunction(value)) return false;
  if (isString(value)) return EMPTY_STRING_REGEXP.test(value);
  if (isArray(value)) return value.length === 0;
  if (isDate(value)) return false;
  if (isObject(value)) {
    return Object.keys(value).length === 0;
  }
  return false;
};

const capitalize = (str) => (isString(str) && str.length ? str[0].toUpperCase() + str.slice(1) : str);

// validate.js %{key} interpolation
const formatStr = (str, values) => {
  if (!isString(str)) return str;
  return str.replace(/%\{([^}]+)\}/g, (_m, key) => String(values[key]));
};

// validate.js prettify — used to turn attribute names into readable labels
const prettify = (input) => {
  if (isNumber(input)) {
    return (input * 100) % 1 === 0 ? `${input}` : parseFloat(Math.round(input * 100) / 100).toFixed(2);
  }
  if (isArray(input)) return input.map(prettify).join(', ');
  if (isObject(input)) return input.toString();
  return `${input}`
    .replace(/([^\s])\.([^\s])/g, '$1 $2')
    .replace(/\\+/g, '')
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, (_m, a, b) => `${a} ${b.toLowerCase()}`)
    .toLowerCase();
};

const stringifyValue = (value) => prettify(value);

// `result` — allows constraints/options to be functions (validate.js feature)
const result = (value, ...args) => (isFunction(value) ? value(...args) : value);

const uniq = (arr) => arr.filter((item, idx) => arr.indexOf(item) === idx);

// --- safe regex validators backed by yup (linear-time patterns) ---
const YUP_EMAIL = Yup.string().email();
const YUP_URL = Yup.string().url();

// --- built-in validators (validate.js-compatible signatures/messages) ---
const validators = {
  presence(value, options) {
    const opts = options === true ? {} : options || {};
    const blank = opts.allowEmpty !== false ? !isDefined(value) : isEmpty(value);
    if (blank) {
      return opts.message || "can't be blank";
    }
    return undefined;
  },

  length(value, options) {
    // Empty (undefined/null) values are allowed
    if (!isDefined(value)) return undefined;
    const opts = options || {};
    const errors = [];
    const { length } = value;
    if (!isNumber(length)) {
      return opts.message || 'has an incorrect length';
    }
    if (isNumber(opts.is) && length !== opts.is) {
      errors.push(formatStr(opts.wrongLength || 'is the wrong length (should be %{count} characters)', { count: opts.is }));
    }
    if (isNumber(opts.minimum) && length < opts.minimum) {
      errors.push(formatStr(opts.tooShort || 'is too short (minimum is %{count} characters)', { count: opts.minimum }));
    }
    if (isNumber(opts.maximum) && length > opts.maximum) {
      errors.push(formatStr(opts.tooLong || 'is too long (maximum is %{count} characters)', { count: opts.maximum }));
    }
    if (errors.length) return opts.message || errors;
    return undefined;
  },

  numericality(value, options) {
    if (!isDefined(value)) return undefined;
    const opts = options || {};
    const errors = [];
    const checks = {
      greaterThan: (a, b) => a > b,
      greaterThanOrEqualTo: (a, b) => a >= b,
      equalTo: (a, b) => a === b,
      lessThan: (a, b) => a < b,
      lessThanOrEqualTo: (a, b) => a <= b,
      divisibleBy: (a, b) => a % b === 0,
    };

    let num = value;
    if (opts.noStrings !== true && isString(num) && !isEmpty(num)) {
      num = +num;
    }
    if (!isNumber(num)) {
      return opts.message || opts.notValid || 'is not a number';
    }
    if (opts.onlyInteger && !isInteger(num)) {
      return opts.message || opts.notInteger || 'must be an integer';
    }
    Object.keys(checks).forEach((name) => {
      const count = opts[name];
      if (isNumber(count) && !checks[name](num, count)) {
        const key = `not${capitalize(name)}`;
        const msg = opts[key] || 'must be %{type} %{count}';
        errors.push(formatStr(msg, { count, type: prettify(name) }));
      }
    });
    if (opts.odd && num % 2 !== 1) errors.push(opts.notOdd || 'must be odd');
    if (opts.even && num % 2 !== 0) errors.push(opts.notEven || 'must be even');
    if (errors.length) return opts.message || errors;
    return undefined;
  },

  format(value, options) {
    let opts = options;
    if (isString(opts) || opts instanceof RegExp) opts = { pattern: opts };
    opts = opts || {};
    const message = opts.message || 'is invalid';
    if (!isDefined(value)) return undefined;
    if (!isString(value)) return message;
    // `pattern` must be a RegExp literal supplied by the caller. We deliberately
    // do not compile a RegExp from a string here: a dynamically-built regex is a
    // ReDoS risk, so callers pass /literals/ (which are statically auditable).
    const { pattern } = opts;
    if (!(pattern instanceof RegExp)) return message;
    // Defence-in-depth cap on the user-controlled value to bound worst-case
    // backtracking, mirroring the email/url caps above.
    if (value.length > 4096) return message;
    const match = pattern.exec(value);
    if (!match || match[0].length !== value.length) return message;
    return undefined;
  },

  email(value, options) {
    const opts = options === true ? {} : options || {};
    const message = opts.message || 'is not a valid email';
    if (!isDefined(value)) return undefined;
    if (!isString(value)) return message;
    // Defence-in-depth length cap (RFC 5321 max is 254); yup's regex is linear anyway.
    if (value.length > 320) return message;
    return YUP_EMAIL.isValidSync(value) ? undefined : message;
  },

  url(value, options) {
    const opts = options === true ? {} : options || {};
    const message = opts.message || 'is not a valid url';
    if (!isDefined(value)) return undefined;
    if (!isString(value)) return message;
    if (value.length > 2048) return message;
    return YUP_URL.isValidSync(value) ? undefined : message;
  },
};

// --- error pipeline (validate.js-compatible) ---
const convertErrorMessages = (errorInfos, options) => {
  const fullMessages = options.fullMessages !== false;
  const out = [];
  errorInfos.forEach((info) => {
    let error = result(info.error, info.value, info.attribute, info.options, info.attributes, info.globalOptions);
    if (!isString(error)) {
      out.push(info);
      return;
    }
    if (error[0] === '^') {
      error = error.slice(1);
    } else if (fullMessages) {
      error = `${capitalize(prettify(info.attribute))} ${error}`;
    }
    error = error.replace(/\\\^/g, '^');
    error = formatStr(error, { value: stringifyValue(info.value) });
    out.push({ ...info, error });
  });
  return out;
};

const expandMultipleErrors = (errorInfos) => {
  const out = [];
  errorInfos.forEach((info) => {
    if (isArray(info.error)) {
      info.error.forEach((message) => out.push({ ...info, error: message }));
    } else {
      out.push(info);
    }
  });
  return out;
};

const formatters = {
  grouped(errorInfos) {
    const grouped = {};
    errorInfos.forEach((info) => {
      (grouped[info.attribute] = grouped[info.attribute] || []).push(info.error);
    });
    Object.keys(grouped).forEach((attr) => {
      grouped[attr] = uniq(grouped[attr]);
    });
    return grouped;
  },
  flat(errorInfos) {
    return uniq(errorInfos.map((info) => info.error));
  },
};

const runValidations = (attributes, constraints, options) => {
  const results = [];
  Object.keys(constraints).forEach((attr) => {
    const value = attributes[attr];
    const attrConstraints = result(constraints[attr], value, attributes, attr, options, constraints);
    Object.keys(attrConstraints || {}).forEach((validatorName) => {
      const validator = validate.validators[validatorName];
      if (!validator) {
        throw new Error(`Unknown validator ${validatorName}`);
      }
      let validatorOptions = attrConstraints[validatorName];
      validatorOptions = result(validatorOptions, value, attributes, attr, options, constraints);
      if (!validatorOptions) return;
      results.push({
        attribute: attr,
        value,
        validator: validatorName,
        globalOptions: options,
        attributes,
        options: validatorOptions,
        error: validator.call(validator, value, validatorOptions, attr, attributes, options),
      });
    });
  });
  return results;
};

const processValidationResults = (errorInfos, options) => {
  let errors = errorInfos.filter((info) => !isEmpty(info.error));
  errors = expandMultipleErrors(errors);
  errors = convertErrorMessages(errors, options);
  const format = options.format || 'grouped';
  if (!isFunction(formatters[format])) {
    throw new Error(`Unknown format ${format}`);
  }
  const out = formatters[format](errors);
  return isEmpty(out) ? undefined : out;
};

function validate(attributes, constraints, options = {}) {
  const results = runValidations(attributes, constraints, options);
  // This implementation is synchronous; the app does not use promise validators.
  return processValidationResults(results, options);
}

validate.validators = validators;
validate.single = (value, constraints, options) =>
  validate({ single: value }, { single: constraints }, { ...options, format: 'flat', fullMessages: false });
validate.isEmpty = isEmpty;
validate.isInteger = isInteger;
validate.prettify = prettify;
validate.capitalize = capitalize;
validate.format = formatStr;

export { isInteger };
export default validate;
